import { DriveProblem, SetCondition } from "./dataMap.utils";

export interface KubernetesSysInfo {
  buildDate: Date;
  gitCommit: string;
  gitVersion: string;
  major: string;
  minor: string;
  platform: string;
}

export interface Info {
  bootTime: number;
  hostId: string;
  hostname: string;
  kernelArch: string;
  kernelVersion: string;
  os: string;
  platform: string;
  platformFamily: string;
  platformVersion: string;
  procs: number;
  uptime: number;
  virtualizationRole: string;
  virtualizationSystem: string;
}

export interface Sensor {
  sensorCritical: number;
  sensorHigh: number;
  sensorKey: string;
  temperature: number;
}

export interface CAOSInfo {
  addr: string;
  info: Info;
  sensors: Sensor[];
}

export interface Cpu2 {
  cache_size: number;
  cores: number;
  family: string;
  flags: string[];
  mhz: number;
  microcode: string;
  model: string;
  model_name: string;
  physical_id: string;
  stepping: number;
  vendor_id: string;
}

export interface Cpu {
  addr: string;
  cpus: Cpu2[];
  is_freq_gov_perf: boolean;
}

export interface Partition {
  device: string;
  fs_type: string;
  inode_free: number;
  inode_total: number;
  mount_fs_type: string;
  mount_options: string;
  mountpoint: string;
  space_free: number;
  space_total: number;
}

export interface ServerPartitions {
  addr: string;
  partitions: Partition[];
}

export interface Iocounters {
  readBytes: number;
  readCount: number;
  writeBytes: number;
  writeCount: number;
}

export interface MemInfo {
  data: number;
  hwm: number;
  locked: number;
  rss: number;
  stack: number;
  swap: number;
  vms: number;
}

export interface MemMap {
  anonymous: number;
  path: string;
  privateClean: number;
  privateDirty: number;
  pss: number;
  referenced: number;
  rss: number;
  sharedClean: number;
  sharedDirty: number;
  size: number;
  swap: number;
}

export interface NumCtxSwitches {
  involuntary: number;
  voluntary: number;
}

export interface PageFaults {
  childMajorFaults: number;
  childMinorFaults: number;
  majorFaults: number;
  minorFaults: number;
}

export interface Times {
  cpu: string;
  guest: number;
  guestNice: number;
  idle: number;
  iowait: number;
  irq: number;
  nice: number;
  softirq: number;
  steal: number;
  system: number;
  user: number;
}

export interface Procinfo {
  addr: string;
  cmd_line: string;
  cpu_percent: number;
  create_time: number;
  cwd: string;
  exec_path: string;
  gids: number[];
  iocounters: Iocounters;
  is_background: boolean;
  is_running: boolean;
  mem_info: MemInfo;
  mem_maps: MemMap[];
  mem_percent: number;
  name: string;
  nice: number;
  num_connections: number;
  num_ctx_switches: NumCtxSwitches;
  num_fds: number;
  num_threads: number;
  page_faults: PageFaults;
  pid: number;
  status: string;
  tgid: number;
  times: Times;
  uids: number[];
  username: string;
}

export interface Meminfo {
  addr: string;
  available: number;
  limit: number;
  total: number;
}

export interface CASys {
  kubernetes?: KubernetesSysInfo;
  osinfo?: CAOSInfo[];
  cpus: Cpu[];
  partitions?: ServerPartitions[];
  procinfo: Procinfo[];
  meminfo: Meminfo[];
}

export interface TimeInfo {
  current_time: string;
  roundtrip_duration: number;
  time_zone: string;
}

export interface ConfigItem {
  rlimit_max: number;
  time_info: TimeInfo;
}

export interface CAConfig {
  addr: string;
  config: ConfigItem;
}

// Health Analysis
export interface ClusterAnalysis {
  version: string;
  timestamp: string;
  code?: string;
  message?: string;
  minio: CAMinIO;
  sys?: CASys;
  config: CAConfig[];
}

export interface ApiCalls {
  AppendFile: number;
  CheckParts: number;
  CreateFile: number;
  Delete: number;
  DeleteAbandonedParts: number;
  DeleteVersion: number;
  DeleteVersions: number;
  DeleteVol: number;
  DiskInfo: number;
  ListDir: number;
  ListVols: number;
  MakeVol: number;
  MakeVolBulk: number;
  ReadAll: number;
  ReadFile: number;
  ReadFileStream: number;
  ReadMultiple: number;
  ReadVersion: number;
  ReadXL: number;
  RenameData: number;
  RenameFile: number;
  StatInfoFile: number;
  StatVol: number;
  UpdateMetadata: number;
  VerifyFile: number;
  WalkDir: number;
  WriteAll: number;
  WriteMetadata: number;
}

export interface DiskInfo {
  acc_time_ns: number;
  count: number;
}

export interface WriteAll {
  acc_time_ns: number;
  count: number;
}

export interface ReadAll {
  acc_time_ns: number;
  count: number;
}

export interface RenameData {
  acc_time_ns: number;
  count: number;
}

export interface StatVol {
  acc_time_ns: number;
  count: number;
}

export interface WriteMetadata {
  acc_time_ns: number;
  count: number;
}

export interface ReadVersion {
  acc_time_ns: number;
  count: number;
}

export interface LastMinute {
  DiskInfo?: DiskInfo;
  ReadAll?: ReadAll;
  ReadVersion?: ReadVersion;
  RenameData?: RenameData;
  StatVol?: StatVol;
  WriteAll?: WriteAll;
  WriteMetadata?: WriteMetadata;
  ListVols: Record<any, any>;
  ReadXL: Record<any, any>;
  WalkDir: Record<any, any>;
}

export interface Metrics {
  apiCalls: ApiCalls;
  lastMinute: LastMinute;

  totalErrorsAvailability: number;
  totalErrorsTimeout: number;
}

export interface FeatureStats {
  min: number;
  max: number;
  mean: number;
  stdDev: number;
}

export interface HealInfo {
  id: string;
  heal_id: string;
  pool_index: number;
  set_index: number;
  disk_index: number;
  endpoint: string;
  path: string;
  started: Date;
  last_update: Date;
  objects_total_count: number;
  objects_total_size: number;
  items_healed: number;
  items_failed: number;
  bytes_done: number;
  bytes_failed: number;
  objects_healed: number;
  objects_failed: number;
  current_bucket: string;
  current_object: string;
  queued_buckets: string[];
  healed_buckets: string[];
}

export interface CAServerDrive {
  endpoint: string;
  path: string;
  state: string;
  uuid: string;
  major: number;
  minor: number;
  totalspace: number;
  usedspace: number;
  availspace: number;
  pool_index: number;
  set_index: number;
  disk_index: number;
  healing: boolean;
  metrics: Metrics;
  drivePath: string;
  fs_type: string;
  free_inodes: number;
  used_inodes: number;
  heal_info?: HealInfo;

  // computed fields
  set_condition?: SetCondition;
  inode_free: number;
  server?: CAInfoServer; // temporarily used to build server pools from drives
  problems?: DriveProblem[];
}

export type StatusButtonState = "green" | "yellow" | "red" | "grey";

export interface CAInfoServer {
  state: string;
  endpoint: string;
  uptime: number;
  version: string;
  commidID: string;
  network: { [key: string]: string };
  drives?: CAServerDrive[];

  onlineDrives: number;
  totalDrives: number;
  totalNetworks: number;
  networksOnline: number;
  driveStatus: StatusButtonState;
  networkStatus: StatusButtonState;
  serverCpus: Cpu[];
  poolNumber?: number; //used in ui to build server list

  // computed fields
  offlineDrivesCount?: number;
  healingDrivesCount?: number;
  drivesRunningOutOfSpaceCount?: number;
  drivesRunningOutOfInodesCount?: number;
  drivesWithErrorsCount?: number;
}

export interface InfoBackend {
  backendType: string;
  offlineDisks: number;
  onlineDisks: number;
  rrSCParity: number;
  standardSCParity: number;
  totalDrivesPerSet: number[];
  totalSets: number[];
}

export interface CAInfo {
  domain: string[];
  servers: CAInfoServer[];
  backend: InfoBackend;
  usage: any;
  buckets: any;
  objects: any;
}

export interface CAMinIO {
  info: CAInfo;
}

export enum detMode {
  "server",
  "erasureSet",
  "drive",
  null,
}
